import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Scale, Ruler, CalendarDays } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { api, ageFrom, fmtDate, fullName } from "@/lib/api";
import { AnagraficaTab } from "@/components/patient/AnagraficaTab";
import { VisitsTab } from "@/components/patient/VisitsTab";
import { AnamnesisTab } from "@/components/patient/AnamnesisTab";
import { NotesTab } from "@/components/patient/NotesTab";
import { DietTab } from "@/components/patient/DietTab";
import { PatientAppointmentsTab } from "@/components/patient/PatientAppointmentsTab";
import { AttachmentsTab } from "@/components/patient/AttachmentsTab";
import { PaymentsTab } from "@/components/patient/PaymentsTab";
import { ChatBox } from "@/components/ChatBox";

const TABS = [
  ["anagrafica", "Anagrafica"], ["visite", "Visite e misure"], ["patologie", "Anamnesi"], ["documenti", "Documenti ed esami"],
  ["appunti", "Appunti privati"], ["dieta", "Piano nutrizionale"], ["appuntamenti", "Appuntamenti"], ["pagamenti", "Pagamenti"], ["messaggi", "Messaggi"],
];

const Header = ({ rec }) => {
  const p = rec.patient;
  const last = rec.visits[rec.visits.length - 1];
  const first = rec.visits[0];
  const diff = last?.weight && first?.weight && rec.visits.length > 1 ? (last.weight - first.weight).toFixed(1) : null;
  const heightV = [...rec.visits].reverse().find((v) => v.height)?.height || p.height_cm;
  const bmi = last?.weight && heightV ? (last.weight / (heightV / 100) ** 2).toFixed(1) : null;
  return (
    <div className="card flex flex-wrap items-center gap-6">
      <div className="grid h-16 w-16 place-items-center rounded-full bg-[#3d655b] text-xl font-extrabold text-white">{p.first_name[0]}{p.last_name[0]}</div>
      <div className="min-w-0 flex-1">
        <h1 className="text-2xl font-extrabold tracking-tight sm:text-3xl" data-testid="patient-name">{fullName(p)}</h1>
        <div className="mt-1 text-sm text-slate-500">
          {ageFrom(p.birth_date) != null ? `${ageFrom(p.birth_date)} anni · ` : ""}{p.email || "nessuna email"}{p.phone ? ` · ${p.phone}` : ""}
        </div>
      </div>
      <div className="flex flex-wrap gap-6 text-sm">
        <div><div className="label-xs flex items-center gap-1"><Scale className="h-3 w-3" /> Peso attuale</div><div className="mt-1 text-xl font-extrabold" data-testid="patient-current-weight">{last?.weight ? `${last.weight} kg` : "—"}</div></div>
        <div><div className="label-xs">Variazione</div><div className={`mt-1 text-xl font-extrabold ${diff < 0 ? "text-emerald-600" : diff > 0 ? "text-amber-600" : ""}`}>{diff != null ? `${diff > 0 ? "+" : ""}${diff} kg` : "—"}</div></div>
        <div><div className="label-xs flex items-center gap-1"><Ruler className="h-3 w-3" /> BMI</div><div className="mt-1 text-xl font-extrabold">{bmi || "—"}</div></div>
        <div><div className="label-xs flex items-center gap-1"><CalendarDays className="h-3 w-3" /> Ultima visita</div><div className="mt-1 text-xl font-extrabold">{fmtDate(last?.date, { day: "2-digit", month: "short" })}</div></div>
      </div>
    </div>
  );
};

export default function PatientDetail() {
  const { id } = useParams();
  const [rec, setRec] = useState(null);
  const [tab, setTab] = useState("anagrafica");
  const load = useCallback(() => api.get(`/patients/${id}`).then((r) => setRec(r.data)), [id]);
  useEffect(() => { load(); }, [load]);

  if (!rec) return <div className="h-40 animate-pulse rounded-2xl bg-white" />;

  return (
    <div className="fade-up space-y-6" data-testid="patient-detail-page">
      <Link to="/pazienti" className="no-print inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-[#3d655b]" data-testid="back-to-patients"><ArrowLeft className="h-4 w-4" /> Pazienti</Link>
      <div className="no-print"><Header rec={rec} /></div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="no-print h-auto w-full justify-start gap-1 overflow-x-auto rounded-full bg-white p-1 shadow-sm">
          {TABS.map(([k, l]) => (
            <TabsTrigger key={k} value={k} data-testid={`tab-${k}`}
              className="rounded-full px-4 py-2 text-sm font-semibold data-[state=active]:bg-[#3d655b] data-[state=active]:text-white">{l}</TabsTrigger>
          ))}
        </TabsList>
        <div className="mt-6">
          <TabsContent value="anagrafica"><AnagraficaTab rec={rec} reload={load} /></TabsContent>
          <TabsContent value="visite"><VisitsTab rec={rec} reload={load} /></TabsContent>
          <TabsContent value="patologie"><AnamnesisTab rec={rec} reload={load} /></TabsContent>
          <TabsContent value="documenti"><AttachmentsTab rec={rec} reload={load} /></TabsContent>
          <TabsContent value="appunti"><NotesTab rec={rec} reload={load} /></TabsContent>
          <TabsContent value="dieta"><DietTab rec={rec} reload={load} /></TabsContent>
          <TabsContent value="appuntamenti"><PatientAppointmentsTab rec={rec} reload={load} /></TabsContent>
          <TabsContent value="pagamenti"><PaymentsTab rec={rec} /></TabsContent>
          <TabsContent value="messaggi">{tab === "messaggi" && <ChatBox url={`/patients/${id}/messages`} me="admin" prefix="patient-chat" />}</TabsContent>
        </div>
      </Tabs>
    </div>
  );
}
